import { useForm, UseFormOptions, UseFormMethods } from 'react-hook-form';
import { useCallback, useEffect, useRef } from 'react';
import { ActionCreatorWithPayload, Selector } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';

interface IUseFormEnhanced {
  methods: UseFormMethods;
  syncFormAction: VoidFunction;
}

export const useFormEnhanced = <V>(
  syncFormValuesAction: ActionCreatorWithPayload<V>,
  syncFormErrorsAction: ActionCreatorWithPayload<Record<string, string>>,
  valuesSelector: Selector<RootState, V>,
  errorsSelector: Selector<RootState, Record<string, string>>,
  options?: UseFormOptions,
  isPageLoading: boolean = false,
): IUseFormEnhanced => {
  const dispatch: AppDispatch = useDispatch();
  const initialValues = useSelector(valuesSelector);
  const initialErrors = useSelector(errorsSelector);
  const history = useHistory();
  const errorsRef = useRef<any>(null);

  const methods = useForm({
    ...options,
    // This is necessary to reset the table columns to default view when the user switches pages.
    defaultValues: { ...initialValues, selectedColumns: undefined },
  });

  const {
    getValues,
    setError,
    formState: { errors },
  } = methods;

  errorsRef.current = errors;

  useEffect(() => {
    if (!isPageLoading && initialErrors) {
      Object.entries(initialErrors).forEach(([fieldName, errorMessage]) => {
        setError(fieldName, { message: errorMessage, type: 'validate' });
      });
    }
  }, [setError, initialErrors, isPageLoading]);

  const syncFormAction = useCallback(() => {
    dispatch(syncFormValuesAction(getValues() as V));
    dispatch(syncFormErrorsAction(getErrorMap(errorsRef.current)));
  }, [dispatch, getValues, syncFormValuesAction, syncFormErrorsAction]);

  useEffect(() => {
    const unsubscribe = history.listen(() => {
      syncFormAction();
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getErrorMap = (errors: any): Record<string, string> => {
    const errorMap: Record<string, string> = {};

    const traverse = (obj: any) => {
      if (!obj || typeof obj !== 'object') return;

      Object.entries(obj).forEach(([, value]: any) => {
        if (!value) return;

        if (typeof value === 'object') traverse(value);

        if (value.ref?.name && value.type === 'validate' && (value.ref?.value || value.ref?.value === 0))
          errorMap[value.ref.name] = value.message ?? '';
      });
    };

    traverse(errors);

    return errorMap;
  };

  return { methods, syncFormAction };
};
